<template>
    <div>
        <v-container>

            <!-- TITLE -->
            <v-row v-if="!hideTitle">
                <v-col cols="12">
                    <div class="title text-center">
                        {{ defaultExercise ? $t('common.edit') : $t('common.create') }}
                        {{ `${$t('common.new')} ${$tc('exercises.exercises', 1)}` }}
                    </div>
                </v-col>
            </v-row>

            <!-- BOTTOM BUTTONS -->
            <v-row v-if="!hideButtons && !bottomBtn" class="bottom-container">
                <v-col cols="12" class="text-right">
                    <v-btn :disabled="!isValid" :loading="saving" @click="next" color="primary"
                        class="black--text normal-btn" depressed>
                        {{ $t('common.save') }}
                    </v-btn>
                </v-col>
            </v-row>

            <!-- EXERCISE -->
            <v-row>
                <v-col cols="12">
                    <v-row>
                        <v-col cols="12">
                            <kimsa-text-field :label="$t('exercises.exercise_name')"
                                :placeholder="$t('exercises.exercise_name_example')" :value="name"
                                @change="name = $event" dense no-elevation bolder-label force-value />
                        </v-col>
                        <v-col cols="12">
                            <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                                :label="$t('exercises.tags')" bolder-label force-value />
                        </v-col>
                        <v-col cols="12">
                            <exercise-types-picker ref="typesPicker" :default-categories="categories"
                                :default-types="types" @change:categories="categories = $event"
                                @change:types="types = $event" force-value>
                                <choose-equipments dropdown hide-title :label="$t('equipments.equipments')"
                                    :selected="equipments" @change="equipments = $event" />
                            </exercise-types-picker>
                        </v-col>
                        <v-col cols="12">
                            <v-divider class="my-2" />
                        </v-col>
                        <v-col cols="12" class="pb-0">
                            <div class="label">
                                {{ $t('exercises.add_video_and_image') }}
                            </div>
                            <kimsa-toggle class="yt-toggle-row" :value="isYoutubeVideo"
                                @change="isYoutubeVideo = $event; cleanOptionVideo()">
                                <span class="yt-toggle-label">{{ $t('common.from_youtube') }}</span>
                            </kimsa-toggle>
                        </v-col>
                        <v-col cols="12" v-show="isYoutubeVideo">
                            <kimsa-text-field :label="$t('common.link')" :placeholder="$t('common.write_here')"
                                bolder-label dense @blur="youtubeUrl = getYouTubeVideoId(youtubeUrl)"
                                :value="youtubeUrl" @change="youtubeUrl = $event" :icon="youtubeUrl ? 'mdi-youtube' : ''" @click:append="goToVideo(youtubeUrl)" />
                        </v-col>
                        <v-col cols="12" v-show="!isYoutubeVideo">
                            <div class="upload-row">
                            <file-uploader ref="imgUploader" mime-types="image/*"
                                @success="onImgUploaded" relative-path="images/routines" :limit-size="2"
                                @change="onChangeImg">
                                <template slot="button">
                                    <div class="img-container">
                                        <div v-if="imgName" class="img-preview"
                                            :style="`background-image: url(${imgUrl})`">
                                            <div class="img-name" @click.stop="clearImg">
                                                {{ imgName }}
                                                <v-icon x-small color="error">$close</v-icon>
                                            </div>
                                        </div>
                                        <div class="img-content" v-else>
                                            <v-icon size="28">$upload_image</v-icon>
                                            <div class="load-image-title">
                                                {{ $t('common.load_image') }}
                                            </div>
                                            <div class="load-image-subtitle">
                                                {{ $t('common.load_image_max_size') }} <span
                                                    class="load-image-subtitle bolder">2MB</span>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </file-uploader>
                            <file-uploader ref="videoUploader" @success="onVideoUploaded"
                                relative-path="images/routines" :limit-size="limitSize" @change="onChangeVideo"
                                :max-duration="maxVideoSeconds" :duration-tolerance="videoLimits.tolerance">
                                <template slot="button">
                                    <div class="img-container">
                                        <div v-if="videoName" class="img-preview"
                                            :style="`background-image: url(${videoThumb})`">
                                            <div class="img-name" @click.stop="clearVideo">
                                                {{ videoName }}
                                                <v-icon x-small color="error">$close</v-icon>
                                            </div>
                                        </div>
                                        <div class="img-content" v-else>
                                            <v-icon size="28">mdi-cloud-upload-outline</v-icon>
                                            <div class="load-image-title">
                                                {{ $t('common.load_video') }}
                                            </div>
                                            <div class="load-image-subtitle">
                                                {{ $t('common.load_video_max_duration') }} <span
                                                    class="load-image-subtitle bolder">{{ maxVideoSeconds }}s</span>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </file-uploader>
                            </div>
                        </v-col>
                    </v-row>
                    <v-sheet v-if="!hideButtons" height="60" color="transparent" />
                </v-col>
            </v-row>
        </v-container>
        <v-row v-if="!hideButtons && bottomBtn">
            <v-col cols="12" class="text-right">
                <v-btn :disabled="!isValid" :loading="saving" @click="next" color="primary"
                    class="black--text normal-btn" depressed>
                    {{ $t('common.save') }}
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import ExerciseTypesPicker from "@/components/coach/routines/ExerciseTypesPicker";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaTagsPicker from "@/components/common/inputs/KimsaTagsPicker";

import { db, deleteStorageFile } from '@/utils/Firebase'
import { videoLimitsFrom } from '@/utils/Plans'
import { invalidateExerciseCache } from '@/utils/ExerciseLibrary'
import FileUploader from "@/components/common/FileUploader";
import ChooseEquipments from "@/components/student/ChooseEquipments";
import KimsaToggle from "@/components/common/inputs/KimsaToggle";
export default {
    name: "ExerciseCreator",
    components: {
        ChooseEquipments, FileUploader, KimsaTagsPicker, KimsaTextField, ExerciseTypesPicker, KimsaToggle
    },
    props: {
        defaultExercise: {
            type: Object,
            default: null
        },
        newExercise: {
            type: Object,
            default: null
        },
        hideButtons: {
            type: Boolean,
            default: false
        },
        hideTitle: {
            type: Boolean,
            default: false
        },
        bottomBtn: {
            type: Boolean,
            default: false
        },
    },
    data(vm) {
        return {
            saving: false,
            exerciseId: null,
            name: '',
            description: '',
            duration: 10,
            unit: vm.$tc('common.seconds', 2),
            videoName: '',
            videoThumb: '',
            imgName: '',
            imgUrl: '',
            equipments: [],
            categories: [],
            types: [],
            tags: [],
            uploadedImg: null,
            uploadedVideo: null,
            isYoutubeVideo: false,
            youtubeUrl: null,
        }
    },
    mounted() {
        this.setDefaults()
    },
    computed: {
        ...mapState(['libraryCoach', 'config']),
        videoLimits(vm = this) {
            return videoLimitsFrom(vm.config)
        },
        maxVideoSeconds(vm = this) {
            return vm.planTier ? (vm.videoLimits[vm.planTier] ?? 0) : 0
        },
        limitSize(vm = this) {
            return vm.videoLimits.maxMB
        },
        unitsOptions() {
            const vm = this
            return [
                vm.$tc('common.seconds', 2),
                vm.$tc('common.minutes', 2),
                vm.$tc('common.hours', 2),
            ]
        },
        isValid() {
            const vm = this
            return !!vm.name && vm.categories.length > 0 && vm.types.length > 0;
        },
        companyId(vm = this) {
            if (vm.accountType === 'coach') return vm.account?.coachSubscription?.companyId || null
            if (vm.isCompany) return vm.currentUser.uid
            return null
        },
        coachId(vm = this) {
            if (vm.accountType === 'coach') return vm.currentUser.uid
            if (vm.isCompany) return vm.libraryCoach?.id || null
            return null
        },
        exercisePayload() {
            const vm = this
            return {
                name: vm.name || '',
                owner: vm.account && vm.account.appTeam ? 'app' : 'user',
                coachId: vm.defaultExercise?.coachId || vm.coachId,
                companyId: vm.defaultExercise?.companyId || vm.companyId,
                description: vm.description || '',
                duration: vm.duration || '',
                unit: vm.unit || '',
                equipments: vm.equipments || [],
                categories: vm.categories || [],
                categoryKeys: (vm.categories || []).map(c => vm.categoryKey(c)).filter(Boolean),
                types: vm.types || [],
                tags: vm.tags || [],
                img: vm.uploadedImg?.url || vm.defaultExercise?.img || '',
                imgSavePath: vm.uploadedImg?.savePath || vm.defaultExercise?.imgSavePath || '',
                video: vm.uploadedVideo || vm.defaultExercise?.video || null,
                youtubeUrl: vm.youtubeUrl || null,
            }
        }
    },
    methods: {
        next() {
            this.save()
        },
        async onImgUploaded({ downloadURL, savePath }) {
            const vm = this
            vm.uploadedImg = { url: downloadURL, savePath: savePath }
        },
        async onVideoUploaded({ downloadURL, savePath }) {
            const vm = this
            vm.uploadedVideo = { tempUrl: downloadURL, savePath: savePath }
        },
        goBack() {
            this.$emit('cancel')
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
                if (vm.imgUrl.includes('blob:')) await vm.$refs.imgUploader.upload()
                if (vm.videoThumb.includes('blob:')) await vm.$refs.videoUploader.upload()

                let response = null
                if (vm.defaultExercise) {
                    const oldImgSavePath = vm.defaultExercise.imgSavePath
                    response = await db.collection('exercises')
                        .doc(vm.defaultExercise.objectID)
                        .update(vm.exercisePayload)
                    if (vm.uploadedImg?.savePath && oldImgSavePath && vm.uploadedImg.savePath !== oldImgSavePath) {
                        await deleteStorageFile(oldImgSavePath)
                    }
                } else {
                    response = await db.collection('exercises')
                        .add(vm.exercisePayload)

                    vm.exerciseId = response.id
                }

                invalidateExerciseCache()
                vm.toast(vm.$t('exercises.saved'))
                const id = vm.defaultExercise && vm.defaultExercise.objectID || vm.exerciseId
                vm.$emit('success', {
                    ...vm.exercisePayload,
                    objectID: id,
                })
            }, 'saving')
        },
        setDefaults() {
            const vm = this

            if (vm.newExercise) {
                vm.name = vm.newExercise.name || ''
                vm.categories = vm.newExercise.categories || []
                vm.types = vm.newExercise.types || []
                return
            }

            if (!vm.defaultExercise) return

            vm.name = vm.defaultExercise.name || ''
            vm.description = vm.defaultExercise.description || ''
            vm.duration = vm.defaultExercise.duration || 10
            vm.unit = vm.defaultExercise.unit || vm.$tc('common.seconds', 2)
            vm.categories = vm.defaultExercise.categories || []
            vm.types = vm.defaultExercise.types || []
            vm.tags = vm.defaultExercise.tags || []
            vm.equipments = vm.defaultExercise.equipments || []
            vm.imgName = vm.getFileName(vm.defaultExercise?.imgSavePath)
            vm.imgUrl = vm.defaultExercise.img || ''
            vm.videoName = vm.getFileName(vm.defaultExercise.video?.savePath)
            vm.videoThumb = vm.defaultExercise.video && vm.defaultExercise.video.thumb || ''
            vm.isYoutubeVideo = vm.defaultExercise.youtubeUrl ? true : false
            vm.youtubeUrl = vm.defaultExercise.youtubeUrl || null
        },
        getFileName(savePath) {
            if (!savePath) return ''
            let arraySplit = savePath.split('/')
            return arraySplit[arraySplit.length - 1].split('-')[1]
        },
        reset() {
            const vm = this
            vm.name = ''
            vm.description = ''
            vm.duration = 10
            vm.unit = vm.$tc('common.seconds', 2)
            vm.categories = []
            vm.equipments = []
            vm.types = []
            vm.tags = []
            vm.imgName = ''
            vm.imgUrl = ''
            vm.videoName = ''
            vm.videoThumb = ''
            vm.uploadedImg = null
            vm.uploadedVideo = null
            vm.youtubeUrl = null
            vm.isYoutubeVideo = false
            if (vm.$refs.typesPicker) vm.$refs.typesPicker.reset()
            if (vm.$refs.imgUploader) vm.$refs.imgUploader.reset()
            if (vm.$refs.videoUploader) vm.$refs.videoUploader.reset()
        },
        onChangeImg({ name, url }) {
            const vm = this
            vm.imgName = name
            vm.imgUrl = url
        },
        clearImg() {
            const vm = this
            vm.imgName = ''
            vm.imgUrl = ''
            vm.uploadedImg = null
            if (vm.$refs.imgUploader) vm.$refs.imgUploader.reset()
        },
        onChangeVideo({ name, url, thumb }) {
            const vm = this
            vm.videoName = name
            vm.videoThumb = thumb || url
        },
        clearVideo() {
            const vm = this
            vm.videoName = ''
            vm.videoThumb = ''
            vm.uploadedVideo = null
            if (vm.$refs.videoUploader) vm.$refs.videoUploader.reset()
        },
        getYouTubeVideoId(url) {
            const pattern = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
            const match = url?.match(pattern)
            return match?.[1] ? match[1] : null
        },
        cleanOptionVideo() {
            const vm = this
            vm.youtubeUrl = null
            vm.clearVideo()
        },
        goToVideo(videoId) {
            const vm = this
            window.open('https://www.youtube.com/watch?v=' + videoId, '_blank')
        },
    },
    watch: {
        newExercise() {
            this.setDefaults()
        },
        defaultExercise() {
            this.setDefaults()
        },
        isValid(val) {
            this.$emit('change:validation', val)
        },
        saving(val) {
            this.$emit('loading', val)
        }
    },
}
</script>

<style scoped>
.bottom-container {
    margin-top: 40px;
    background: var(--v-surface-base);
    border-top: 1px solid var(--v-outline-base);
    position: fixed;
    right: 0;
    left: 0;
    bottom: 0;
    padding: 8px 16px 16px 16px;
    z-index: 2;
}

.label {
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    font-weight: 600;
}

.yt-toggle-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.yt-toggle-label {
    font-size: 14px;
    color: var(--v-txtPrimary-base);
}

.upload-row {
    display: flex;
    gap: 12px;
}

.upload-row >>> .video-input-container {
    flex: 1;
    display: block;
}

.upload-row >>> .btn-container {
    display: block;
}

.img-container {
    width: 100%;
    height: 130px;
    border: 1.5px dashed var(--v-outline2-base);
    border-radius: 10px;
    position: relative;
    cursor: pointer;
    overflow: hidden;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color .15s, background .15s;
}

.img-container:hover {
    border-color: var(--v-accent-base);
    background: rgba(51, 153, 153, .05);
}

.img-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
    padding: 0 12px;
}

.img-content .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.load-image-title {
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;
    color: var(--v-txtPrimary-base);
}

.load-image-subtitle {
    font-weight: 400;
    font-size: 11px;
    line-height: 14px;
    color: var(--v-txtMuted-base);
}

.load-image-subtitle.bolder {
    font-weight: 600;
}

.img-preview {
    position: absolute;
    inset: 0;
    background-color: var(--v-surface2-base);
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
}

.img-name {
    font-weight: 500;
    font-size: 10px;
    line-height: 14px;
    color: #fff;
    position: absolute;
    top: 6px;
    right: 6px;
    background-color: rgba(0, 0, 0, .6);
    padding: 2px 6px;
    border-radius: 4px;
    max-width: 91%;
    text-align: right;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 2px;
}
</style>